import { io, Socket } from 'socket.io-client';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface ConnectionEvents {
  onStatusChange?: (status: ConnectionStatus) => void;
  onReconnectAttempt?: (attempt: number, maxAttempts: number) => void;
  onReconnected?: () => void;
  onReconnectFailed?: () => void;
  onDisconnected?: (reason: string) => void;
}

export class SocketManagerSimple {
  private socket: Socket | null = null;
  private serverUrl: string;
  private connectionEvents: ConnectionEvents = {};
  private _status: ConnectionStatus = 'disconnected';
  private isIntentionalDisconnect: boolean = false;
  private wasConnectedBefore: boolean = false; // Флаг для отслеживания переподключения
  
  public reconnectAttempts: number = 0;
  public maxReconnectAttempts: number = 5;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  private setStatus(status: ConnectionStatus) {
    this._status = status;
    this.connectionEvents.onStatusChange?.(status);
  }

  setConnectionEvents(events: ConnectionEvents) {
    this.connectionEvents = events;
  }

  // Ожидание подключения сокета
  waitForConnection(timeoutMs: number = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        console.log('[Socket] Already connected');
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        console.error('[Socket] waitForConnection timeout');
        reject(new Error('Wait for connection timeout'));
      }, timeoutMs);

      const checkConnection = () => {
        if (this.socket?.connected) {
          clearTimeout(timeout);
          console.log('[Socket] Connection confirmed');
          resolve();
        } else {
          setTimeout(checkConnection, 50);
        }
      };

      checkConnection();
    });
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('🔌 Connecting to:', this.serverUrl);
      this.setStatus('connecting');
      this.isIntentionalDisconnect = false;
      this.wasConnectedBefore = false;
      
      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
      });

      const timeout = setTimeout(() => {
        this.setStatus('failed');
        reject(new Error('Connection timeout after 10s'));
      }, 10000);

      // Используем событие connect для определения (пере)подключения
      this.socket.on('connect', () => {
        clearTimeout(timeout);
        console.log('✅ Socket connected:', this.socket?.id, 'wasConnectedBefore:', this.wasConnectedBefore);
        this.reconnectAttempts = 0;
        this.setStatus('connected');
        
        // Если это переподключение - вызываем callback
        if (this.wasConnectedBefore) {
          console.log('[Socket] This is a reconnection, calling onReconnected');
          this.connectionEvents.onReconnected?.();
        } else {
          this.wasConnectedBefore = true;
          resolve();
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Connection error:', error);
        // Не реджектим сразу - socket.io попробует переподключиться
      });

      this.socket.on('disconnect', (reason) => {
        console.warn('⚠️ Disconnected:', reason, 'intentional:', this.isIntentionalDisconnect);
        
        if (this.isIntentionalDisconnect) {
          this.setStatus('disconnected');
          return;
        }

        // Причины, при которых socket.io НЕ переподключается автоматически
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
          this.setStatus('disconnected');
          this.connectionEvents.onDisconnected?.(reason);
        } else {
          // При других причинах socket.io попробует переподключиться
          this.setStatus('reconnecting');
          this.connectionEvents.onDisconnected?.(reason);
        }
      });

      this.socket.io.on('reconnect_attempt', (attempt) => {
        console.log(`🔄 Reconnect attempt ${attempt}/${this.maxReconnectAttempts}`);
        this.reconnectAttempts = attempt;
        this.setStatus('reconnecting');
        this.connectionEvents.onReconnectAttempt?.(attempt, this.maxReconnectAttempts);
      });

      // Не используем reconnect от manager - используем connect событие выше
      this.socket.io.on('reconnect', (attempt) => {
        console.log('✅ Manager reconnect event after', attempt, 'attempts (handled by connect event)');
      });

      this.socket.io.on('reconnect_failed', () => {
        console.error('❌ Reconnection failed after max attempts');
        this.setStatus('failed');
        this.connectionEvents.onReconnectFailed?.();
      });

      this.socket.on('error', (error) => {
        console.error('❌ Socket error:', error);
      });
    });
  }

  disconnect(): void {
    this.isIntentionalDisconnect = true;
    this.connectionEvents = {}; // Очищаем все callback'и
    if (this.socket) {
      // Отключаем reconnect перед disconnect
      this.socket.io.opts.reconnection = false;
      this.socket.disconnect();
      this.socket = null;
    }
    this.setStatus('disconnected');
  }

  // Принудительная остановка reconnect без вызова callback'ов
  forceStop(): void {
    this.isIntentionalDisconnect = true;
    this.connectionEvents = {}; // Очищаем все callback'и чтобы не вызывались
    if (this.socket) {
      this.socket.io.opts.reconnection = false;
      this.socket.removeAllListeners();
      this.socket.io.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this._status = 'disconnected';
  }

  emit<T = any>(event: string, data?: any): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const timeoutId = setTimeout(() => {
        reject(new Error('Request timeout'));
      }, 10000);

      this.socket.emit(event, data, (response: any) => {
        clearTimeout(timeoutId);
        if (response && response.success) {
          resolve(response);
        } else {
          reject(new Error(response?.error || 'Unknown error'));
        }
      });
    });
  }

  on(event: string, callback: (...args: any[]) => void): void {
    this.socket?.on(event, callback);
  }

  off(event: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      this.socket?.off(event, callback);
    } else {
      this.socket?.off(event);
    }
  }

  get id(): string | undefined {
    return this.socket?.id;
  }

  get connected(): boolean {
    return this.socket?.connected || false;
  }
}

export default SocketManagerSimple;
